/* eslint-disable @typescript-eslint/no-explicit-any */
import InfoIcon from "@mui/icons-material/Info";
import classNames from "classnames";
import { t } from "i18next";
import { snapshot, useSnapshot } from "valtio";

import React from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { toast } from "react-toastify";

import {
    AddIcon,
    AddIconWrapper,
} from "@components/OrderTaking/Components/Icons/AddIcon";
import { RemoveIcon } from "@components/OrderTaking/Components/Icons/RemoveIcon";
import { getProductTitle } from "@components/OrderTaking/Helpers";
import {
    addToOrderworkflow,
    ClearWorkflowItem,
    memoizedRewardSelectionIndex,
    SelectProduct,
    selectUserRewards,
    setIsModification,
    setUserGifts,
    setWorkflowData,
} from "@components/OrderTaking/Store/feature";

import { PONGO_LOYALTY_TYPE } from "@constants";

import { kioskStore } from "@pages/Kiosk/store";

import { useContrastColor } from "./contrastColor";
import {
    formatRewardExpirationDate,
    handleCodePromoSymbole,
} from "./loyaltyHelper";
import { LoyaltyInfoModal } from "./loyaltyInfoModal";
import "./promo.css";
import { RemoveSelectedRewardModal } from "./RemoveSelectedRewardModal";
import {
    PromoCodeCardContainer,
    PromoCodeOnlyCardContainer,
} from "./styledCss";

type LoyaltyCardType = {
    couponData?: any;
    isPromoCode?: boolean;
};

function LoyaltyCardComponent({
    couponData,
    isPromoCode,
}: LoyaltyCardType): JSX.Element {
    const dispatch = useDispatch();
    // `globalcard`, `nestedCategories` and `userGifts` are only read when the
    // card is tapped, so they are pulled from the store on click instead of
    // being subscribed to: subscribing to them re-rendered every card on any
    // order change.
    const reduxStore = useStore();

    // the same product listed in another reward category can only be taken once
    const isSelectedInAnotherCategory = useSelector((state: any) => {
        if (isPromoCode === true || couponData?.isSelected) {
            return false;
        }
        const { selectedCategoryIdsByIuud } =
            memoizedRewardSelectionIndex(state);
        const categoryIds = selectedCategoryIdsByIuud.get(couponData.iuud);

        return (
            categoryIds !== undefined &&
            categoryIds.size >
                (categoryIds.has(couponData.rewardCategoryId) ? 1 : 0)
        );
    });

    // The category is full: only the products already chosen stay clickable, so
    // the customer can swap one for another. Counted on the stored list, not on
    // the displayed one, which is filtered by sale mode visibility.
    const rewardCategoryQty = Number(couponData?.rewardCategoryQty);
    const isQuotaReached = useSelector((state: any) => {
        if (
            isPromoCode === true ||
            couponData?.isSelected ||
            !(rewardCategoryQty > 0)
        ) {
            return false;
        }
        const { selectionCountByCategoryId } =
            memoizedRewardSelectionIndex(state);

        return (
            (selectionCountByCategoryId.get(couponData.rewardCategoryId) ?? 0) >=
            rewardCategoryQty
        );
    });

    const loyaltyAccount = useSelector(
        (state: any) => state.orderSlice.userRewards.loyaltyAccount
    );
    const totalPriceWithOutFidelity = useSelector(
        (state: any) => state.orderSlice.order.totalPriceWithOutFidelity
    );

    const isRewardsDisabled =
        !isPromoCode &&
        ((couponData.rewardLoyaltyPoints as number) > loyaltyAccount ||
            isSelectedInAnotherCategory ||
            isQuotaReached);

    // grey and disable click on promo code and rewards card
    const isBtnDisabled =
        isRewardsDisabled ||
        (isPromoCode && couponData.minOrder > totalPriceWithOutFidelity) ||
        (couponData.maxOrder !== 0 &&
            couponData.maxOrder < totalPriceWithOutFidelity);

    const [isInfoShown, setIsInfoShown] = React.useState(false);
    const [isRewardUnSelected, setIsRewardUnSelected] = React.useState(false);
    const [isPromoCodeDisabled, setIsPromoCodeDisabled] = React.useState(false);

    const handlePromoCodeOnClick = () => {
        const { userGifts } = (reduxStore.getState() as any).orderSlice;

        if (!couponData?.isSelected) {
            if (isBtnDisabled) {
                setIsPromoCodeDisabled(true);
                return;
            }
            const promo: any = userGifts.map((item: any) => {
                return item.id === couponData.id
                    ? { ...item, isSelected: true }
                    : { ...item, isSelected: false };
            });

            dispatch(setUserGifts(promo));
        } else {
            const promo: any = userGifts.map((item: any) => {
                return { ...item, isSelected: false };
            });

            dispatch(setUserGifts(promo));
        }
    };

    const handleRewardsOnClick = () => {
        const { globalcard, nestedCategories } = (
            reduxStore.getState() as any
        ).orderSlice;
        const { selectedSaleModeId } = snapshot(kioskStore);

        if (!couponData?.isSelected) {
            if (isBtnDisabled) {
                const message = isSelectedInAnotherCategory
                    ? t("This product is already chosen in another reward.")
                    : isQuotaReached
                    ? t(
                          "You can only choose {{qty}} product(s) in this reward.",
                          { qty: rewardCategoryQty }
                      )
                    : t("Loyalty balance is not enough.");

                toast.info(message, {
                    className: "responsive-toast",
                    position: toast.POSITION.BOTTOM_RIGHT,
                    autoClose: 3000,
                    theme: "colored",
                    closeOnClick: true,
                    hideProgressBar: false,
                });
                return;
            }
            const isHaveOneOption =
                couponData?.optionsKiosk?.Options.length === 1 &&
                couponData?.optionsKiosk?.Options[0].listOptions.length > 0
                    ? couponData?.optionsKiosk?.Options[0].listOptions
                          .length === 1
                        ? true
                        : false
                    : false;

            const initialRewardOrder = {
                ...couponData,
                shopCart: {},
                price: 0,
                isReward: true,
                displayTitle: couponData?.name,
                tva: couponData.advancedItemPrice?.saleModeVatRates[
                    selectedSaleModeId
                ]
                    ? couponData.advancedItemPrice?.saleModeVatRates[
                          selectedSaleModeId
                      ]
                    : 0,
                isInformationModeActivated:
                    globalcard.categories[
                        nestedCategories[nestedCategories.length - 1]
                    ].isInformationModeActivated ?? false,
                selectedOptions: isHaveOneOption
                    ? [
                          getProductTitle(
                              couponData.optionsKiosk.Options[0].listOptions[0]
                                  .name
                          ),
                      ]
                    : [],
                selectedOptionsUuid: isHaveOneOption
                    ? [
                          couponData.optionsKiosk.Options[0].iuud,
                          couponData.optionsKiosk.Options[0].listOptions[0]
                              .iuud,
                      ]
                    : [],
                selectedIndexOptions: isHaveOneOption
                    ? [
                          couponData.optionsKiosk.Options[0].listOptions[0]
                              .optionIndex,
                      ]
                    : [],
                date: Date.now(),
                isHiddenBasicComp: couponData?.isHiddenBasicComp ?? false,
                optMsg: couponData?.optMsg ?? null,
                selectedBasicComposition: [],
            };
            if (
                couponData.modifier != "" &&
                (couponData.haveWorkflow || couponData.haveBasicComposition)
            ) {
                dispatch(setWorkflowData([]));
                dispatch(SelectProduct(initialRewardOrder));
                dispatch(ClearWorkflowItem());
                dispatch(setIsModification(false));
                dispatch(addToOrderworkflow(initialRewardOrder));
            } else {
                // add reward selection and subtract used loyalty point
                dispatch(selectUserRewards(initialRewardOrder));
            }
        } else {
            dispatch(selectUserRewards(couponData));
        }
    };

    return (
        <React.Fragment>
            {(isInfoShown || isPromoCodeDisabled) && (
                <LoyaltyInfoModal
                    couponData={couponData}
                    setIsInfoShown={setIsInfoShown}
                    isInfoShown={isInfoShown}
                    isPromoCodeDisabled={isPromoCodeDisabled}
                    setIsPromoCodeDisabled={setIsPromoCodeDisabled}
                />
            )}
            {isRewardUnSelected && (
                <RemoveSelectedRewardModal
                    setIsRewardUnSelected={setIsRewardUnSelected}
                    isRewardUnSelected={isRewardUnSelected}
                    handleOnclick={handleRewardsOnClick}
                />
            )}
            {isPromoCode ? (
                <CodePromoCard
                    couponData={couponData}
                    isBtnDisabled={isBtnDisabled}
                    handleOnclick={handlePromoCodeOnClick}
                    isInfoShown={isInfoShown}
                    setIsInfoShown={setIsInfoShown}
                />
            ) : (
                <RewardsCard
                    couponData={couponData}
                    isBtnDisabled={isBtnDisabled}
                    handleOnclick={handleRewardsOnClick}
                    setIsRewardUnSelected={setIsRewardUnSelected}
                />
            )}
        </React.Fragment>
    );
}

/* Memoised: a card only has to re-render when its own coupon or one of the few
   values it subscribes to changes, not on every order dispatch. */
export const LoyaltyCard = React.memo(LoyaltyCardComponent);

type RewardsCardType = {
    couponData: any;
    isBtnDisabled?: boolean;
    handleOnclick: () => void;
    setIsRewardUnSelected: React.Dispatch<React.SetStateAction<boolean>>;
};
export function RewardsCard({
    couponData,
    isBtnDisabled,
    handleOnclick,
    setIsRewardUnSelected,
}: RewardsCardType): JSX.Element {
    return (
        <div
            style={{
                opacity: isBtnDisabled && !couponData?.isSelected ? 0.4 : 1,
            }}
        >
            <div
                className={classNames("cardStyle swiper-slide", {
                    selectedCard: couponData?.isSelected,
                })}
                style={{
                    border: couponData?.isSelected ? "4px solid #000000" : "",
                }}
                onClick={() => {
                    if (couponData?.isSelected) {
                        setIsRewardUnSelected(true);
                    } else {
                        handleOnclick();
                    }
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: "20px",
                        right: "20px",
                        zIndex: 2,
                    }}
                >
                    {couponData?.isSelected ? (
                        <RemoveIcon
                            width={54}
                            height={54}
                            onClick={() => setIsRewardUnSelected(true)}
                        />
                    ) : (
                        <button className="icons-button__clz " title="add icon">
                            <AddIcon
                                opacity={isBtnDisabled ? "0.3" : "1"}
                                width={54}
                                height={54}
                            />
                        </button>
                    )}
                </div>
                <div className="image-content">
                    <span className="overlay"></span>
                    <div className="card-image">
                        <img
                            src={couponData.urlImage}
                            alt={couponData.urlImage}
                            className="card-img"
                            width={150}
                            height={150}
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>
                <div className="card-content">
                    <h2
                        className="reward-name preview-text"
                        style={{ marginTop: "1rem" }}
                    >
                        {couponData.name}
                    </h2>
                </div>
            </div>
        </div>
    );
}

type CodePromoCardType = {
    couponData: any;
    isBtnDisabled?: boolean;
    handleOnclick: () => void;
    setIsInfoShown: React.Dispatch<React.SetStateAction<boolean>>;
    isInfoShown: boolean;
};
export function CodePromoCard({
    couponData,
    isBtnDisabled,
    handleOnclick,
    setIsInfoShown,
    isInfoShown,
}: CodePromoCardType): JSX.Element {
    const {
        project: {
            template: {
                pages: {
                    subSteps: {
                        customerBenefit: {
                            options: { rewards },
                        },
                    },
                },
            },
        },
        customerLanguage,
    } = useSnapshot(kioskStore);

    const inputColor = useContrastColor();

    const codePromoName =
        couponData.name[customerLanguage.name.toUpperCase()] !== undefined &&
        couponData.name[customerLanguage.name.toUpperCase()] != ""
            ? couponData.name[customerLanguage.name.toUpperCase()]
            : couponData.name["default"];

    function handleInfoIconClick() {
        setIsInfoShown(!isInfoShown);
    }
    return (
        <>
            {rewards.active ? (
                <PromoCodeCardContainer
                    style={{
                        border: couponData?.isSelected
                            ? "4px solid #000000"
                            : "",
                    }}
                >
                    <div
                        className="code-promo-title"
                        onClick={handleOnclick}
                        style={{
                            opacity: isBtnDisabled ? 0.2 : 1,
                            color: inputColor ?? "white",
                        }}
                    >
                        <h2 className="discount-name">
                            {couponData.discountValue}
                            {handleCodePromoSymbole(
                                Number(couponData.discountType)
                            )}
                        </h2>
                        <span className="preview-text">{codePromoName}</span>
                    </div>
                    <div className="code-promo-icons">
                        <InfoIcon
                            style={{
                                cursor: "pointer",
                                width: "30px",
                                height: "30px",
                                color: inputColor ?? "white",
                            }}
                            onClick={handleInfoIconClick}
                        />
                        {couponData?.isSelected ? (
                            <RemoveIcon
                                width={54}
                                height={54}
                                onClick={handleOnclick}
                            />
                        ) : (
                            <AddIconWrapper
                                opacity={isBtnDisabled ? 0.3 : 1}
                                width={54}
                                height={54}
                                onClick={handleOnclick}
                            />
                        )}
                    </div>
                </PromoCodeCardContainer>
            ) : (
                <PromoCodeOnlyCardContainer
                    style={{
                        border: couponData?.isSelected
                            ? "4px solid #000000"
                            : "",
                    }}
                >
                    <div
                        className="code-promo-only-title"
                        onClick={handleOnclick}
                        style={{
                            opacity: isBtnDisabled ? 0.2 : 1,
                            color: inputColor ?? "white",
                        }}
                    >
                        <h2 className="discount-name">
                            {couponData.discountValue}
                            {handleCodePromoSymbole(
                                Number(couponData.discountType)
                            )}
                        </h2>
                        <span className="preview-text">{codePromoName}</span>
                    </div>
                    <div className="code-promo-only-icons">
                        <InfoIcon
                            style={{
                                cursor: "pointer",
                                width: "45px",
                                height: "45px",
                                color: inputColor ?? "white",
                            }}
                            onClick={handleInfoIconClick}
                        />
                        {couponData?.isSelected ? (
                            <RemoveIcon
                                width={54}
                                height={54}
                                onClick={handleOnclick}
                            />
                        ) : (
                            <AddIconWrapper
                                opacity={isBtnDisabled ? 0.3 : 1}
                                width={54}
                                height={54}
                                onClick={handleOnclick}
                            />
                        )}
                    </div>
                </PromoCodeOnlyCardContainer>
            )}
        </>
    );
}

export function CategoryCard({ categoryData }: categoryCardType): JSX.Element {
    const { customerLanguage, user } = useSnapshot(kioskStore);
    const inputColor = useContrastColor();

    const isFreeReward = Number(categoryData.loyalty_points) === 0;
    const rewardQty = Number(categoryData.qty);

    // An offer tells the customer how many products they get to pick in the
    // list, point based rewards are always a single product.
    const title = isFreeReward
        ? [
              t("Free"),
              rewardQty > 0 ? t("{{qty}} to choose", { qty: rewardQty }) : "",
          ]
              .filter((part) => part !== "")
              .join(" · ")
        : `${categoryData.loyalty_points} ${t("points")}`;

    // Godia sends a translations map for the category name, Pongo a plain label.
    const categoryName =
        typeof categoryData.reward_name === "string"
            ? categoryData.reward_name
            : categoryData.reward_name?.[
                  customerLanguage.name.toUpperCase()
              ] ?? "";

    // The expiration date is only provided by the Pongo loyalty program and is
    // only relevant for free rewards, point based ones never expire.
    const isExpirationDateShown =
        user.loyaltyType === PONGO_LOYALTY_TYPE && isFreeReward;
    const expirationDate = formatRewardExpirationDate(categoryData.date_end);

    return (
        <div className="cardStyle swiper-slide reward-category-card">
            <div className="image-content">
                <span className="category-card-overly"></span>
                <div className="card-image">
                    <img
                        src={categoryData.image_url}
                        alt={categoryData.image_url}
                        className="category-card-img"
                        width={150}
                        height={150}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            </div>
            <div className="category-card-content">
                <h2
                    className="name preview-text"
                    style={{ color: inputColor ?? "white" }}
                >
                    {categoryName}
                </h2>
                <h6
                    className="category-card-points"
                    style={{ color: inputColor ?? "white" }}
                >
                    {title}
                </h6>
                {isExpirationDateShown && (
                    <h6
                        className="category-card-expiration"
                        style={{ color: inputColor ?? "white" }}
                    >
                        {expirationDate !== ""
                            ? t("Until {{expirationDate}}", {
                                  expirationDate,
                              })
                            : t("No expiration date")}
                    </h6>
                )}
            </div>
        </div>
    );
}
